import { type UIEventHandler, useEffect, useRef, useState } from "react";
import { useInvoiceListParams } from "@/accounts-payable/ap-url-state";
import { useGetInvoices } from "@/accounts-payable/query";
import type {
  ApActionType,
  ApDayField,
  ApDayRange,
  ApInvoiceStatus,
  ApReviewState,
} from "@/accounts-payable/types";
import { useDebounce } from "@/core/hooks";

export const INVOICE_FILTERS = [
  { key: "all", label: "All", statuses: [] },
  {
    key: "needs_attention",
    label: "Needs attention",
    statuses: ["match_failed", "parse_failed"],
  },
  { key: "needs_job", label: "Needs a job", statuses: ["match_failed"] },
  { key: "unreadable", label: "Couldn't read", statuses: ["parse_failed"] },
  {
    key: "processing",
    label: "Processing",
    statuses: ["pending_parse", "parsing", "parsed", "matching"],
  },
  { key: "matched", label: "Matched", statuses: ["matched"] },
  {
    key: "needs_review",
    label: "Needs review",
    statuses: [],
    reviewStates: ["needs_review"],
  },
  {
    key: "reviewed",
    label: "Reviewed",
    statuses: [],
    reviewStates: ["reviewed"],
  },
] satisfies InvoiceFilter[];

export interface InvoiceFilter {
  key: string;
  label: string;
  statuses: ApInvoiceStatus[];
  reviewStates?: ApReviewState[];
  inQuickbooks?: boolean;
}

/** Offered only while the business has QuickBooks (see `useInvoicesTab`). */
const QUICKBOOKS_INVOICE_FILTERS: InvoiceFilter[] = [
  ...INVOICE_FILTERS,
  {
    key: "not_in_quickbooks",
    label: "Not in QuickBooks",
    statuses: [],
    inQuickbooks: false,
  },
];

interface ListPosition {
  filterKey: string;
  scrollTop: number;
  scrollLeft: number;
  pageCount: number;
}

const positions = new Map<string, ListPosition>();

const filterKey = (params: ReturnType<typeof useInvoiceListParams>["params"]) =>
  JSON.stringify([
    params.status,
    params.q,
    params.date,
    params.days,
    params.action,
    params.assignee,
  ]);

export const useInvoicesTab = (
  businessId: string | null,
  { hasQuickbooks = false }: { hasQuickbooks?: boolean } = {},
) => {
  const { params, assigneeIds, setParams, setSearch } = useInvoiceListParams();
  const key = filterKey(params);
  const savedPosition = useRef(
    (businessId && positions.get(businessId)?.filterKey === key
      ? positions.get(businessId)
      : null) ?? null,
  ).current;
  const [restored, setRestored] = useState(() => !savedPosition);
  const viewportRef = useRef<HTMLDivElement>(null);
  const debouncedSearch = useDebounce(params.q, 300);
  const statusFilters: InvoiceFilter[] = hasQuickbooks
    ? QUICKBOOKS_INVOICE_FILTERS
    : INVOICE_FILTERS;
  const activeFilter = statusFilters.find(
    (filter) => filter.key === params.status,
  );
  const query = useGetInvoices(businessId, {
    statuses: activeFilter?.statuses,
    reviewStates: activeFilter?.reviewStates,
    inQuickbooks: activeFilter?.inQuickbooks,
    search: debouncedSearch,
    added: params.date === "added" ? params.days : null,
    reviewed: params.date === "reviewed" ? params.days : null,
    actionTypes: params.action,
    assigneeIds,
  });
  const loadedPageCount = query.data?.pages.length ?? 0;
  const needsMorePages =
    !!savedPosition &&
    query.hasNextPage &&
    loadedPageCount < savedPosition.pageCount;
  const restoring = !restored;
  const awaitingPosition =
    restoring &&
    !query.isError &&
    (query.isPending ||
      query.isFetching ||
      query.isPlaceholderData ||
      needsMorePages);
  const { fetchNextPage } = query;

  useEffect(() => {
    if (restoring && needsMorePages && !query.isFetching && !query.isError) {
      void fetchNextPage();
    }
  }, [
    restoring,
    needsMorePages,
    loadedPageCount,
    query.isFetching,
    query.isError,
    fetchNextPage,
  ]);

  // SimpleTable's own mount effects run first, including its scroll reset.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (
      !savedPosition ||
      !restoring ||
      awaitingPosition ||
      query.isError ||
      !viewport
    )
      return;
    if (
      savedPosition.scrollTop >
        viewport.scrollHeight - viewport.clientHeight + 1 &&
      query.hasNextPage
    ) {
      void fetchNextPage();
      return;
    }
    viewport.scrollTop = savedPosition.scrollTop;
    viewport.scrollLeft = savedPosition.scrollLeft;
    setRestored(true);
  }, [
    restoring,
    awaitingPosition,
    query.isError,
    query.hasNextPage,
    savedPosition,
    loadedPageCount,
    fetchNextPage,
  ]);

  const canSaveList =
    !restoring &&
    !query.isPlaceholderData &&
    !query.isPending &&
    debouncedSearch === params.q;

  // Sync list metadata even when loading another page produces no scroll event.
  useEffect(() => {
    if (!businessId || !canSaveList) return;
    const saved = positions.get(businessId);
    positions.set(businessId, {
      filterKey: key,
      scrollTop: saved?.filterKey === key ? saved.scrollTop : 0,
      scrollLeft: saved?.filterKey === key ? saved.scrollLeft : 0,
      pageCount: loadedPageCount,
    });
  }, [businessId, canSaveList, key, loadedPageCount]);

  const onViewportScroll: UIEventHandler<HTMLDivElement> = (event) => {
    if (!businessId || !canSaveList) return;
    positions.set(businessId, {
      filterKey: key,
      scrollTop: event.currentTarget.scrollTop,
      scrollLeft: event.currentTarget.scrollLeft,
      pageCount: loadedPageCount,
    });
  };

  const startOver = () => {
    setRestored(true);
    if (viewportRef.current) viewportRef.current.scrollTop = 0;
  };
  const change = (changes: Parameters<typeof setParams>[0]) => {
    void setParams(changes);
    startOver();
  };

  return {
    query,
    viewportRef,
    onViewportScroll,
    awaitingPosition,
    restoring,
    statusFilters,
    filter: activeFilter ? params.status : "all",
    search: params.q,
    dayField: params.date,
    dayRange: params.days,
    actionTypes: params.action as ApActionType[],
    assigneeTokens: params.assignee,
    debouncedSearch,
    setFilter: (status: string) => change({ status }),
    setSearch: (q: string) => {
      void setSearch(q);
      startOver();
    },
    setDayField: (date: ApDayField) => change({ date }),
    setDayRange: (days: ApDayRange | null) => change({ days }),
    setActionTypes: (action: ApActionType[]) => change({ action }),
    setAssigneeTokens: (assignee: string[]) => change({ assignee }),
  };
};
